import { useState, useEffect, useCallback } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

const center = { lat: 39.3518631, lng: -120.2718358 };
// Create a bounding box with sides ~100km away from the center point
const tahoeServiceAreaBounds = {
  north: center.lat + 1,
  south: center.lat - 1,
  east: center.lng + 1,
  west: center.lng - 1,
};

interface UseAddressAutocompleteOptions {
  bounds?: google.maps.LatLngBoundsLiteral | null;
}

const useAddressAutocomplete = (
  options: UseAddressAutocompleteOptions = {},
) => {
  const { bounds = tahoeServiceAreaBounds } = options;
  const placesLib = useMapsLibrary("places");
  const geocodingLib = useMapsLibrary("geocoding");

  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [placePredictions, setPlacePredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  useEffect(() => {
    if (!placesLib) return;

    setAutocompleteService(new placesLib.AutocompleteService());
  }, [placesLib]);

  useEffect(() => {
    if (!geocodingLib) return;

    setGeocoder(new geocodingLib.Geocoder());
  }, [geocodingLib]);

  const getPlacePredictions = useCallback(
    (params: { input: string }) => {
      const { input } = params;

      if (!autocompleteService || !input) {
        setPlacePredictions([]);
        return;
      }

      const request: google.maps.places.AutocompletionRequest = {
        input,
        types: ["address"],
        componentRestrictions: { country: "us" },
        ...(bounds ? { bounds } : {}),
      };

      autocompleteService.getPlacePredictions(
        request,
        (predictions, status) => {
          if (status === placesLib?.PlacesServiceStatus.OK && predictions) {
            setPlacePredictions(predictions);
          } else {
            setPlacePredictions([]);
          }
        },
      );
    },
    [autocompleteService, placesLib, bounds],
  );

  const getAddressComponents = useCallback(
    (
      placeId: string,
    ): Promise<google.maps.GeocoderAddressComponent[] | null> => {
      return new Promise((resolve) => {
        if (!geocoder) {
          resolve(null);
          return;
        }

        geocoder.geocode({ placeId }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            resolve(results[0].address_components);
          } else {
            resolve(null);
          }
        });
      });
    },
    [geocoder],
  );

  return { placePredictions, getPlacePredictions, getAddressComponents };
};

export default useAddressAutocomplete;
